import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Address3HelperService, AddressFormGroup } from '@shared/components/address3/address3-helper.service';
import { SELECT_INVALID_SOLDO_FIELD, collectFormErrors, scrollToFirstResult } from '@shared/formHelpers';
import { ApplicationSegment, Company, CountryMetadata } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService } from '@shared/services/country.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'registered-address',
  templateUrl: './registered-address.component.html',
  styles: ``
})
export class RegisteredAddressComponent implements OnInit {
    private readonly subStepName = "Company Registered Address";
    private companyToSave: Company;
    loading = false;
    form: FormGroup<AddressFormGroup>;
    selectedCountry: string = null;
    onlyActiveMarket: boolean;
    translatedCountries: CountryMetadata[] = [];

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly addressFormHelper: Address3HelperService,
        private readonly countryService: CountryService,
        private readonly translate: TranslateService,
        private readonly destroyRef: DestroyRef
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const company: Company = this.route.snapshot.data.company;
        this.companyToSave = RegistrationHelper.cloneCompany(company);
        this.onlyActiveMarket = this.registrationService.getSegment() === ApplicationSegment.selfServe;
        this.selectedCountry = company?.registeredAddress?.countryISO ?? company?.registeredAddress?.countryISO;
        this.form = this.addressFormHelper.createPrepopulatedAddressFormGroup(company?.registeredAddress);

        this.translatedCountries = this.route.snapshot.data.countryList;
        this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(async () => {
            this.translatedCountries = await this.countryService.getCurrentCountries();
        });
    }

    private isValid(): boolean {
        this.form.markAllAsTouched();
        return this.form.valid;
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.isValid()) {
            this.loading = true;
            const formValues = this.form.value;
            this.companyToSave.registeredAddress = {
                countryISO: this.selectedCountry,
                line1: formValues.line1,
                line2: formValues.line2,
                postalCode: formValues.postalCode,
                city: formValues.city,
                province: formValues.province,
                searchRefCode: formValues.searchRefCode
            };
            this.companyToSave.kybProviderId = null;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            try {
                await this.registrationService.saveCompany(this.companyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.REGISTERED_ADDRESS);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save company failed', e, 'with company', this.companyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD);
        }
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

}
